import { useState, type MouseEvent } from "react";
import {
  AppBar,
  Box,
  Button,
  ButtonBase,
  Drawer,
  Grid,
  IconButton,
  List,
  ListSubheader,
  Modal,
  Popover,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useRecoilValue } from "recoil";

// ============================================================
// Material Icons
// ヘッダー、Drawer、ログアウトなどで使用するアイコン。
// ============================================================
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountCircle from "@mui/icons-material/AccountCircle";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import EventNoteIcon from "@mui/icons-material/EventNoteTwoTone";
import AppsIcon from "@mui/icons-material/Apps";

// ============================================================
// 他システム一覧で使用する画像アイコン
// ============================================================
import icon_kosu from "../assets/kosu_icon.png";
import icon_subcontract from "../assets/subcontract_icon.png";
import icon_has from "../assets/Has_icon.png";
import icon_garage from "../assets/garage_icon.png";
import icon_nonconformity from "../assets/nonconformity_icon.png";

// ============================================================
// 共通コンポーネント、定数、状態管理
// ============================================================
import MenuList from "../component/MenuList";
import ConstList from "../utils/ConstList";
import { useMediaQueryContext } from "../component/MediaQueryProvider";
import { LoginUserAtom } from "../stores/LoginUserAtom";
import { maintenanceState } from "../stores/MaintenanceAtom";
import { designTokens } from "../theme";

// ============================================================
// このコンポーネント内で使用する配色
//
// ログイン後の既存ヘッダー、Drawer、Popoverの見た目を
// まとめて管理する。
// ============================================================
const colors = {
  appBarBg: "#EEF3E7",
  appBarText: designTokens.text.primary,
  appBarMuted: designTokens.text.secondary,
  appBarBorder: "rgba(113, 132, 82, 0.22)",
  appBarHover: "rgba(152, 188, 46, 0.12)",
  appBarAccent: designTokens.brand.dark,
  appBarAccentSoft: designTokens.overlay.brandSoft,
  appBarAccentBorder: designTokens.overlay.brandMedium,

  drawerBg: designTokens.surface.page,
  drawerPaper: designTokens.surface.paper,
  drawerText: designTokens.text.primary,
  drawerMuted: designTokens.text.secondary,
  drawerBorder: designTokens.border.default,
  drawerHover: designTokens.overlay.brandSoft,
  drawerIcon: designTokens.brand.dark,

  popoverBg: designTokens.surface.paper,
  popoverText: designTokens.text.primary,
  popoverBorder: designTokens.border.default,
  popoverHover: designTokens.surface.subtle,

  warningBg: "rgba(217,142,4,0.10)",
  warningBorder: "rgba(217,142,4,0.30)",
  warningText: "#8A5700",

  danger: designTokens.state.error,
};

// ============================================================
// 認証状態によって表示する画面を切り替える親コンポーネント
//
// Auth0が認証状態を確認している間：
//   システム名と無効化されたログインボタンを表示する。
//
// 未ログインの場合：
//   システム名とログインボタンだけを表示する。
//
// ログイン済みの場合：
//   現在のログイン後ヘッダーを表示する。
// ============================================================
export default function MenuAppBar() {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();

  // ----------------------------------------------------------
  // 認証確認中または未ログインの場合
  //
  // ログイン後のメニューやログアウトボタンは生成せず、
  // ログイン前専用画面だけを表示する。
  // ----------------------------------------------------------
  if (isLoading || !isAuthenticated) {
    return (
      <BeforeLoginScreen
        isLoading={isLoading}
        onLogin={() => {
          void loginWithRedirect();
        }}
      />
    );
  }

  // ----------------------------------------------------------
  // Auth0によるログインが完了した場合
  //
  // 既存のログイン後ヘッダーを表示する。
  // ----------------------------------------------------------
  return <AuthenticatedMenuAppBar />;
}

// ============================================================
// ログイン前画面へ渡す値
// ============================================================
type BeforeLoginScreenProps = {
  // Auth0が認証状態を確認しているかどうか
  isLoading: boolean;

  // ログインボタンを押したときに実行する処理
  onLogin: () => void;
};

// ============================================================
// ログイン前専用画面
//
// 表示するもの：
//   ・システム名
//   ・ログインボタン
//
// 操作できるもの：
//   ・ログインボタンのみ
//
// Modalを使用することで、ログイン前に背後のページや
// ボタンへマウス・キーボードでアクセスしにくくしている。
// ============================================================
function BeforeLoginScreen({ isLoading, onLogin }: BeforeLoginScreenProps) {
  return (
    <Modal
      open
      disableEscapeKeyDown
      aria-labelledby="before-login-system-title"
      sx={{
        zIndex: 2000,
      }}
    >
      <Box
        component="main"
        tabIndex={-1}
        sx={{
          position: "fixed",
          inset: 0,
          width: "100%",
          minHeight: "100dvh",
          overflow: "hidden",
          bgcolor: "#0F172A",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          outline: "none",

          // --------------------------------------------------
          // 画面左上の背景装飾
          //
          // クリック対象にならないように
          // pointerEventsを無効化している。
          // --------------------------------------------------
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-28%",
            left: "-15%",
            width: {
              xs: 420,
              sm: 660,
              md: 860,
            },
            height: {
              xs: 420,
              sm: 660,
              md: 860,
            },
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(37, 99, 235, 0.30) 0%, rgba(37, 99, 235, 0.10) 42%, transparent 72%)",
            pointerEvents: "none",
          },

          // --------------------------------------------------
          // 画面右下の背景装飾
          //
          // 左上と同様に、表示専用で操作対象にはしない。
          // --------------------------------------------------
          "&::after": {
            content: '""',
            position: "absolute",
            right: "-18%",
            bottom: "-35%",
            width: {
              xs: 440,
              sm: 700,
              md: 920,
            },
            height: {
              xs: 440,
              sm: 700,
              md: 920,
            },
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(14, 165, 233, 0.20) 0%, rgba(14, 165, 233, 0.06) 45%, transparent 72%)",
            pointerEvents: "none",
          },
        }}
      >
        {/* ----------------------------------------------------
            ログイン前画面の中央コンテンツ

            システム名とログインボタンを縦方向に配置する。
        ----------------------------------------------------- */}
        <Stack
          spacing={{
            xs: 4,
            sm: 5,
          }}
          alignItems="center"
          sx={{
            position: "relative",
            zIndex: 1,
            width: "100%",
            maxWidth: 760,
            textAlign: "center",
          }}
        >
          {/* --------------------------------------------------
              システム名

              ConstList.SYS_TITLEの内容を表示する。
              選択やクリックはできない表示専用要素。
          --------------------------------------------------- */}
          <Typography
            id="before-login-system-title"
            component="h1"
            sx={{
              m: 0,
              color: "#F8FAFC",
              fontSize: {
                xs: "2rem",
                sm: "2.8rem",
                md: "3.4rem",
              },
              fontWeight: 800,
              lineHeight: 1.25,
              letterSpacing: {
                xs: "0.02em",
                sm: "0.04em",
              },
              textShadow: "0 8px 30px rgba(0, 0, 0, 0.28)",
              userSelect: "none",
              pointerEvents: "none",
            }}
          >
            {ConstList.SYS_TITLE}
          </Typography>

          {/* --------------------------------------------------
              ログインボタン
              押すとAuth0のログイン画面へ遷移する。
          --------------------------------------------------- */}
          <Button
            type="button"
            variant="contained"
            disabled={isLoading}
            onClick={onLogin}
            sx={{
              position: "relative",
              minWidth: {
                xs: 190,
                sm: 210,
              },
              minHeight: 52,
              px: 4.5,
              py: 1.35,
              overflow: "hidden",

              borderRadius: 2,
              border: "1px solid rgba(165, 180, 252, 0.35)",

              background:
                "linear-gradient(135deg, #2563EB 0%, #4F46E5 52%, #7C3AED 100%)",
              color: "#F8FAFC",

              fontSize: "1rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "none",

              boxShadow:
                "0 12px 30px rgba(37, 99, 235, 0.30), 0 4px 12px rgba(15, 23, 42, 0.30)",

              transition:
                "transform 0.18s ease, box-shadow 0.18s ease, filter 0.18s ease",

              // ボタン表面の薄い光沢
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(120deg, transparent 15%, rgba(255, 255, 255, 0.18) 45%, transparent 75%)",
                transform: "translateX(-120%)",
                transition: "transform 0.45s ease",
                pointerEvents: "none",
              },

              "&:hover": {
                background:
                  "linear-gradient(135deg, #3B82F6 0%, #6366F1 52%, #8B5CF6 100%)",
                transform: "translateY(-2px)",
                boxShadow:
                  "0 18px 38px rgba(37, 99, 235, 0.38), 0 6px 16px rgba(15, 23, 42, 0.34)",
                filter: "brightness(1.04)",

                "&::before": {
                  transform: "translateX(120%)",
                },
              },

              "&:active": {
                transform: "translateY(0)",
                boxShadow:
                  "0 8px 20px rgba(37, 99, 235, 0.28), 0 3px 8px rgba(15, 23, 42, 0.28)",
              },

              "&:focus-visible": {
                outline: "3px solid rgba(129, 140, 248, 0.55)",
                outlineOffset: 4,
              },

              "&.Mui-disabled": {
                background:
                  "linear-gradient(135deg, rgba(37, 99, 235, 0.45), rgba(124, 58, 237, 0.38))",
                color: "rgba(248, 250, 252, 0.65)",
                borderColor: "rgba(165, 180, 252, 0.16)",
                boxShadow: "none",
              },
            }}
          >
            {isLoading ? "認証情報を確認中" : "ログイン"}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}

// ============================================================
// メンテナンス時刻を「HH:mm」の形式に整える
//
// 値が空の場合や「:」を含まない場合でもエラーを出さず、
// 受け取った値をそのまま返す。
// ============================================================
function formatMaintenanceTime(value: string | null | undefined) {
  if (!value) {
    return "";
  }

  const [hour, minute] = value.split(":");

  if (!hour || !minute) {
    return value;
  }

  return `${hour}:${minute}`;
}

// ============================================================
// ログイン後専用ヘッダー
//
// ログイン済みの場合だけ、このコンポーネントが生成される。
// 現在使用しているヘッダー、Drawer、アプリ一覧、
// ログアウト処理などをここにまとめている。
// ============================================================
function AuthenticatedMenuAppBar() {
  // ----------------------------------------------------------
  // PC表示かどうかを取得する
  //
  // 現在の仕様では、ログイン後ヘッダーはPC表示の場合だけ
  // 描画される。
  // ----------------------------------------------------------
  const { isPcSite } = useMediaQueryContext();

  // ----------------------------------------------------------
  // ログインユーザー情報をRecoilから取得する
  //
  // ヘッダー右側のユーザー名表示に使用する。
  // ----------------------------------------------------------
  const loginUserData = useRecoilValue(LoginUserAtom);

  // ----------------------------------------------------------
  // メンテナンス情報をRecoilから取得する
  //
  // メンテナンスが有効な場合、ヘッダー上へ予定日時を表示する。
  // ----------------------------------------------------------
  const maintenance = useRecoilValue(maintenanceState);

  // ----------------------------------------------------------
  // Auth0のログアウト処理を取得する
  // ----------------------------------------------------------
  const { logout } = useAuth0();

  // ----------------------------------------------------------
  // 左側Drawerの開閉状態
  //
  // true：Drawerを開く
  // false：Drawerを閉じる
  // ----------------------------------------------------------
  const [open, setOpen] = useState(false);

  // ----------------------------------------------------------
  // アプリ一覧Popoverを表示する基準要素
  //
  // nullの場合はPopoverを閉じる。
  // ボタン要素が入っている場合は、その位置にPopoverを表示する。
  // ----------------------------------------------------------
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // ----------------------------------------------------------
  // メンテナンス表示を出すかどうかを判定する
  //
  // displayまたはflagのどちらかが1の場合に表示する。
  // ----------------------------------------------------------
  const isMaintenanceActive =
    maintenance.display === 1 || maintenance.flag === 1;

  // ----------------------------------------------------------
  // メンテナンス開始時刻と終了時刻を表示用に整形する
  // ----------------------------------------------------------
  const START_TIME = formatMaintenanceTime(maintenance.start_time);

  const END_TIME = formatMaintenanceTime(maintenance.end_time);

  // ----------------------------------------------------------
  // 左側Drawerを開く
  // ----------------------------------------------------------
  const handleDrawerOpen = () => {
    setOpen(true);
  };

  // ----------------------------------------------------------
  // 左側Drawerを閉じる
  // ----------------------------------------------------------
  const handleDrawerClose = () => {
    setOpen(false);
  };

  // ----------------------------------------------------------
  // アプリ一覧ボタンを押したときの処理
  //
  // 押されたボタンを基準位置としてPopoverを開く。
  // ----------------------------------------------------------
  const handleAppPopoverOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // ----------------------------------------------------------
  // アプリ一覧Popoverを閉じる
  // ----------------------------------------------------------
  const handleAppPopoverClose = () => {
    setAnchorEl(null);
  };

  // ----------------------------------------------------------
  // Auth0からログアウトする
  //
  // ログアウト完了後は、
  // 現在のドメイン＋SYS_TOPDOMAINへ戻す。
  // ----------------------------------------------------------
  const handleLogout = () => {
    logout({
      returnTo: `${window.location.origin}${ConstList.SYS_TOPDOMAIN}/login`,
    });
  };

  // ----------------------------------------------------------
  // Popoverのアクセシビリティ用ID
  //
  // Popoverが開いているときだけIDを設定する。
  // ----------------------------------------------------------
  const popoverId = anchorEl ? "system-app-popover" : undefined;

  // ----------------------------------------------------------
  // アプリ一覧Popoverへ表示する他システム
  //
  // label：画面に表示する名称
  // icon ：表示する画像
  // url  ：クリック時に別タブで開くURL
  // ----------------------------------------------------------
  const iconItems = [
    {
      label: "工数集計",
      icon: icon_kosu,
      url: "https://system.syowa.com/kosu/home",
    },
    {
      label: "外注費管理",
      icon: icon_subcontract,
      url: "https://system.syowa.com/subcontracting_cost/home",
    },
    {
      label: "安全衛生",
      icon: icon_has,
      url: "https://system2.syowa.com/Health-and-safety/home",
    },
    {
      label: "不適合管理",
      icon: icon_nonconformity,
      url: "https://system2.syowa.com/nonconformity_management/home",
    },
    {
      label: "社有車予約",
      icon: icon_garage,
      url: "https://system2.syowa.com/garage_management/home",
    },
  ];

  // ----------------------------------------------------------
  // 左側Drawerの内容
  //
  // ハンバーガーメニューを押したときに表示する。
  // ページ移動用メニューとログアウトボタンを配置している。
  // ----------------------------------------------------------
  const drawer = (
    <Box
      sx={{
        width: 304,
        minHeight: "100%",
        bgcolor: colors.drawerBg,
        color: colors.drawerText,
        display: "flex",
        flexDirection: "column",

        // Drawer内のList全体に共通する余白
        "& .MuiList-root": {
          py: 0.5,
        },

        // Drawer内のカテゴリ見出し
        "& .MuiListSubheader-root": {
          bgcolor: "transparent",
          color: colors.drawerMuted,
          fontSize: "0.75rem",
          fontWeight: 700,
          lineHeight: "34px",
          letterSpacing: "0.08em",
        },

        // Drawer内の各メニューボタン
        "& .MuiListItemButton-root": {
          mx: 1.25,
          my: 0.25,
          borderRadius: 2,
          color: colors.drawerText,
          transition: "background-color 0.15s ease, color 0.15s ease",

          "&:hover": {
            bgcolor: colors.drawerHover,
          },
        },

        // Drawer内のメニューアイコン
        "& .MuiListItemIcon-root": {
          minWidth: 40,
          color: colors.drawerIcon,
        },

        // Drawer内のメニュー文字
        "& .MuiListItemText-primary": {
          color: colors.drawerText,
          fontSize: "0.92rem",
          fontWeight: 600,
        },

        "& .MuiSvgIcon-root": {
          color: "inherit",
        },
      }}
    >
      {/* ------------------------------------------------------
          Drawer上部

          「メニュー」の見出しと閉じるボタンを表示する。
      ------------------------------------------------------- */}
      <Toolbar
        sx={{
          minHeight: "56px !important",
          px: 1.5,
          display: "flex",
          justifyContent: "space-between",
          borderBottom: `1px solid ${colors.drawerBorder}`,
          bgcolor: colors.drawerPaper,
        }}
      >
        <Typography
          sx={{
            color: colors.drawerText,
            fontWeight: 700,
          }}
        >
          メニュー
        </Typography>

        <IconButton
          type="button"
          onClick={handleDrawerClose}
          size="small"
          aria-label="メニューを閉じる"
          sx={{
            color: colors.drawerMuted,

            "&:hover": {
              bgcolor: colors.drawerHover,
              color: colors.drawerText,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Toolbar>

      {/* ------------------------------------------------------
          Drawer中央のページ移動メニュー

          MenuListコンポーネントへアイコン、名称、
          遷移先URLを渡している。
      ------------------------------------------------------- */}
      <Box
        sx={{
          flex: 1,
          py: 1,
        }}
      >
        <List subheader={<ListSubheader>ホーム</ListSubheader>}>
          <MenuList Icon={<HomeIcon />} ListName="ホーム" Url="/home" />
        </List>

        {/*
          現在のコードに存在する2つ目の「ホーム」メニュー。
          既存構成を維持するため、そのまま残している。

          不要な重複であれば、このListブロック全体を削除できる。
        */}

        <List subheader={<ListSubheader>ユーザー</ListSubheader>}>
          <MenuList
            Icon={<AccountCircle />}
            ListName="マイページ"
            Url="/my_page"
          />

          <MenuList Icon={<SettingsIcon />} ListName="設定" Url="/setting" />
        </List>

        <List subheader={<ListSubheader>その他</ListSubheader>}>
          <MenuList
            Icon={<EventNoteIcon />}
            ListName="リリースノート"
            Url="/ReleaceNote"
          />
        </List>
      </Box>

      {/* ------------------------------------------------------
          Drawer下部のログアウトボタン

          押すとAuth0からログアウトし、
          システムのログイン前画面へ戻る。
      ------------------------------------------------------- */}
      <Box
        sx={{
          p: 1.5,
          borderTop: `1px solid ${colors.drawerBorder}`,
          bgcolor: colors.drawerPaper,
        }}
      >
        <Button
          type="button"
          onClick={handleLogout}
          variant="outlined"
          startIcon={<LogoutIcon />}
          sx={{
            width: "100%",
            py: 1,
            borderRadius: 2,
            borderColor: "rgba(220, 38, 38, 0.35)",
            color: colors.danger,
            fontWeight: 700,
            bgcolor: colors.drawerPaper,

            "&:hover": {
              borderColor: colors.danger,
              bgcolor: "rgba(220, 38, 38, 0.06)",
            },
          }}
        >
          ログアウト
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      {/* ======================================================
          他システム一覧Popover

          ヘッダー右側のアプリアイコンを押したときに表示する。
          各項目を押すと、対象システムを別タブで開く。
      ======================================================= */}
      <Popover
        id={popoverId}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleAppPopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            mt: 1.25,
            width: 360,
            maxWidth: "calc(100vw - 24px)",
            p: 1.25,
            borderRadius: 3,
            bgcolor: colors.popoverBg,
            color: colors.popoverText,
            border: `1px solid ${colors.popoverBorder}`,
            boxShadow: "0 18px 45px rgba(15, 23, 42, 0.18)",
          },
        }}
      >
        <Grid container spacing={1}>
          {iconItems.map((item) => (
            <Grid
              size={{
                xs: 4,
              }}
              key={item.label}
              display="flex"
              justifyContent="center"
            >
              {/* ----------------------------------------------
                  他システムを開くボタン

                  noopenerとnoreferrerを指定し、
                  新しいタブから元画面を操作されないようにする。
              ----------------------------------------------- */}
              <ButtonBase
                type="button"
                onClick={() => {
                  window.open(item.url, "_blank", "noopener,noreferrer");
                }}
                sx={{
                  width: "100%",
                  height: 92,
                  p: 1,
                  borderRadius: 2.5,
                  border: `1px solid ${colors.popoverBorder}`,
                  bgcolor: colors.drawerPaper,
                  color: colors.popoverText,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition:
                    "background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease",

                  "&:hover": {
                    bgcolor: colors.popoverHover,
                    borderColor: colors.appBarAccentBorder,
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Stack spacing={0.75} alignItems="center">
                  <Box
                    component="img"
                    src={item.icon}
                    alt=""
                    sx={{
                      width: 42,
                      height: 42,
                      objectFit: "contain",
                    }}
                  />

                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.popoverText,
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      lineHeight: 1.25,
                      textAlign: "center",
                    }}
                  >
                    {item.label}
                  </Typography>
                </Stack>
              </ButtonBase>
            </Grid>
          ))}
        </Grid>
      </Popover>

      {/* ======================================================
          PC用ログイン後ヘッダー

          現在の仕様に合わせ、isPcSiteがtrueの場合だけ表示する。
      ======================================================= */}
      {isPcSite && (
        <Box
          sx={{
            flexGrow: 1,
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 10,
          }}
        >
          <AppBar
            position="static"
            color="transparent"
            elevation={0}
            sx={{
              color: colors.appBarText,
              borderBottom: `1px solid ${colors.appBarBorder}`,
              backgroundColor: colors.appBarBg,
              backgroundImage: `
                radial-gradient(circle at 12% 25%, rgba(152,188,46,0.18), transparent 26%),
                radial-gradient(circle at 88% 70%, rgba(109,132,78,0.10), transparent 24%),
                linear-gradient(115deg, #F7FAF2 0%, #EEF3E7 46%, #E8EFE0 100%)
              `,
              boxShadow: "0 5px 18px rgba(50,65,35,0.10)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
                opacity: 0.42,
                backgroundImage:
                  "radial-gradient(rgba(83,101,58,0.16) 0.7px, transparent 0.7px)",
                backgroundSize: "14px 14px",
                maskImage:
                  "linear-gradient(90deg, rgba(0,0,0,0.75), transparent 58%)",
                WebkitMaskImage:
                  "linear-gradient(90deg, rgba(0,0,0,0.75), transparent 58%)",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: 3,
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(152,188,46,0.80) 18%, rgba(152,188,46,0.30) 52%, transparent 100%)",
                pointerEvents: "none",
              },
            }}
          >
            <Toolbar
              sx={{
                minHeight: 64,
                gap: 1,
                position: "relative",
                zIndex: 1,
              }}
            >
              {/* ----------------------------------------------
                  Drawerを開くハンバーガーメニューボタン
              ----------------------------------------------- */}
              <IconButton
                type="button"
                size="large"
                edge="start"
                aria-label="メニューを開く"
                onClick={handleDrawerOpen}
                sx={{
                  mr: 0.5,
                  color: colors.appBarAccent,
                  border: `1px solid ${colors.appBarAccentBorder}`,
                  backgroundColor: "rgba(255,255,255,0.58)",

                  "&:hover": {
                    bgcolor: colors.appBarAccentSoft,
                  },
                }}
              >
                <MenuIcon />
              </IconButton>

              {/* ----------------------------------------------
                  左側Drawer

                  ハンバーガーメニューボタンで開き、
                  Drawer外のクリックまたは閉じるボタンで閉じる。
              ----------------------------------------------- */}
              <Drawer
                anchor="left"
                open={open}
                onClose={handleDrawerClose}
                PaperProps={{
                  sx: {
                    width: 304,
                    bgcolor: colors.drawerBg,
                    borderRight: `1px solid ${colors.drawerBorder}`,
                  },
                }}
              >
                {drawer}
              </Drawer>

              {/* ----------------------------------------------
                  ヘッダー左側

                  ホームボタン、システム名、
                  ローカル環境表示を配置する。
              ----------------------------------------------- */}
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {/* ホーム画面へ移動するボタン */}
                <Link
                  to="../home"
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <IconButton
                    aria-label="ホーム"
                    size="medium"
                    sx={{
                      color: colors.appBarAccent,

                      "&:hover": {
                        bgcolor: colors.appBarAccentSoft,
                      },
                    }}
                  >
                    <HomeIcon />
                  </IconButton>
                </Link>

                {/* システム名 */}
                <Typography
                  variant="h6"
                  component="div"
                  noWrap
                  sx={{
                    color: colors.appBarText,
                    fontSize: "1.05rem",
                    fontWeight: 850,
                    letterSpacing: "0.025em",
                    pl: 1.35,
                    position: "relative",
                    textShadow: "0 1px 0 rgba(255,255,255,0.75)",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: "16%",
                      bottom: "16%",
                      width: 4,
                      borderRadius: 999,
                      background: "linear-gradient(180deg, #C5E763, #799B24)",
                      boxShadow: "0 0 12px rgba(152,188,46,0.28)",
                    },
                  }}
                >
                  {ConstList.SYS_TITLE}
                </Typography>

                {/* --------------------------------------------
                    ローカル環境表示

                    DEBUG_LOCALがtrueの場合だけ、
                    システム名の横へLOCALを表示する。
                --------------------------------------------- */}
                {ConstList.DEBUG_LOCAL && (
                  <Box
                    component="span"
                    sx={{
                      px: 1,
                      py: 0.35,
                      borderRadius: 999,
                      bgcolor: "rgba(255,255,255,0.60)",
                      color: colors.appBarAccent,
                      border: `1px solid ${colors.appBarAccentBorder}`,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                    }}
                  >
                    LOCAL
                  </Box>
                )}
              </Box>

              {/* ----------------------------------------------
                  メンテナンス予定

                  maintenance.displayまたはflagが1の場合だけ
                  メンテナンス日時を表示する。
              ----------------------------------------------- */}
              {isMaintenanceActive && (
                <Box
                  sx={{
                    mr: 1,
                    px: 1.5,
                    py: 0.8,
                    borderRadius: 2,
                    bgcolor: colors.warningBg,
                    border: `1px solid ${colors.warningBorder}`,
                    color: colors.warningText,
                    fontSize: "0.86rem",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  メンテナンス予定：
                  {maintenance.day} {START_TIME}～{END_TIME}
                </Box>
              )}

              {/* ----------------------------------------------
                  ログインユーザー名

                  押すとマイページへ移動する。
              ----------------------------------------------- */}
              <Link
                to="../my_page"
                style={{
                  textDecoration: "none",
                }}
              >
                <Button
                  startIcon={<AccountCircle />}
                  sx={{
                    minWidth: 0,
                    px: 1.25,
                    color: colors.appBarText,
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: 2,
                    backgroundColor: "rgba(255,255,255,0.58)",
                    border: `1px solid ${colors.appBarBorder}`,
                    boxShadow: "0 2px 8px rgba(50,65,35,0.05)",

                    "&:hover": {
                      bgcolor: colors.appBarAccentSoft,
                      borderColor: colors.appBarAccentBorder,
                    },
                  }}
                >
                  <Typography
                    component="span"
                    noWrap
                    sx={{
                      maxWidth: 160,
                      color: colors.appBarText,
                      fontSize: "0.92rem",
                      fontWeight: 700,
                    }}
                  >
                    {loginUserData.userName}
                  </Typography>
                </Button>
              </Link>

              {/* ----------------------------------------------
                  設定画面へ移動するボタン
              ----------------------------------------------- */}
              <Link
                to="../setting"
                style={{
                  textDecoration: "none",
                }}
              >
                <IconButton
                  size="large"
                  aria-label="設定"
                  sx={{
                    color: colors.appBarMuted,

                    "&:hover": {
                      color: colors.appBarAccent,
                      bgcolor: colors.appBarAccentSoft,
                    },
                  }}
                >
                  <SettingsIcon />
                </IconButton>
              </Link>

              {/* ----------------------------------------------
                  他システム一覧を開くボタン

                  押すとアプリ一覧Popoverを表示する。
              ----------------------------------------------- */}
              <IconButton
                type="button"
                aria-label="アプリ一覧"
                aria-describedby={popoverId}
                onClick={handleAppPopoverOpen}
                size="large"
                sx={{
                  color: colors.appBarMuted,

                  "&:hover": {
                    color: colors.appBarAccent,
                    bgcolor: colors.appBarAccentSoft,
                  },
                }}
              >
                <AppsIcon />
              </IconButton>

              {/* ----------------------------------------------
                  ヘッダー右端のログアウトボタン

                  押すとAuth0からログアウトする。
              ----------------------------------------------- */}
              <IconButton
                type="button"
                aria-label="ログアウト"
                onClick={handleLogout}
                size="large"
                sx={{
                  color: colors.appBarMuted,

                  "&:hover": {
                    bgcolor: "rgba(220,90,82,0.10)",
                    color: colors.danger,
                  },
                }}
              >
                <LogoutIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
        </Box>
      )}
    </>
  );
}
